import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { agentAPI, appointmentAPI, bookingAPI, claimAPI, walletAPI } from '../../lib/api';
import api from '../../lib/api';
import { formatCurrency, getRankLabel, generateReferralLink } from '../../lib/utils';
import { GlassCard, StatCard, StatusBadge, RankBadge } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
  Building2, Calendar, FileText, Wallet, Users, Copy,
  ArrowRight, TrendingUp, DollarSign, Award, Target, Info,
} from 'lucide-react';

const AgentDashboard = () => {
  const { user } = useAuth();
  const [performance, setPerformance] = useState(null);
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loanKpis, setLoanKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      const [perfRes, apptsRes, bookingsRes, walletRes] = await Promise.all([
        agentAPI.getPerformance(user.id).catch(() => ({ data: {} })),
        appointmentAPI.getAppointments({ status: 'pending' }).catch(() => ({ data: [] })),
        bookingAPI.getBookings().catch(() => ({ data: [] })),
        walletAPI.getWallet().catch(() => ({ data: { balance: 0 } })),
      ]);
      setPerformance(perfRes.data);
      setPendingAppointments(Array.isArray(apptsRes.data) ? apptsRes.data.slice(0, 5) : []);
      setRecentBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data.slice(0, 5) : []);
      setWalletBalance(walletRes.data?.balance || 0);
      api.get('/public/v1/agent/loan-kpis')
        .then((r) => setLoanKpis(r.data))
        .catch(() => setLoanKpis(null));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = generateReferralLink(user?.referral_code);
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="agent-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Agent Dashboard</h1>
          <div className="flex items-center gap-2 sm:gap-3 mt-2 flex-wrap">
            <RankBadge rank={user?.agent_rank} />
            <span className="font-mono text-[#4E50FF] font-medium text-sm sm:text-base">{user?.agent_code}</span>
          </div>
        </div>
        <Button onClick={copyReferralLink} className="bg-[#4E50FF] hover:bg-[#3436CC] text-white self-start sm:self-auto" data-testid="copy-referral-btn">
          <Copy className="w-4 h-4 mr-2" />
          <span className="hidden xs:inline">Copy Referral Link</span>
          <span className="xs:hidden">Referral</span>
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <StatCard title="Total Sales" value={performance?.completed_sales || 0} icon={TrendingUp} />
        <StatCard title="Pending Appointments" value={performance?.pending_appointments || 0} icon={Calendar} />
        <StatCard title="Total Commission" value={formatCurrency(performance?.total_commission_earned || 0)} icon={DollarSign} />
        <StatCard title="Wallet Balance" value={formatCurrency(walletBalance)} subtitle="Points" icon={Wallet} />
      </div>

      {/* Commission Engine V2 — PSC / TLC KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6" data-testid="loan-kpi-tiles">
        <GlassCard className="p-5" data-testid="psc-ytd-tile">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
              Personal Sales Credit (YTD)
              <span title={loanKpis?.psc_ytd?.rule || 'Your own sales where booking-payment, SPA and cash-out all fall in the current year.'}
                    data-testid="psc-info" className="cursor-help">
                <Info className="w-3.5 h-3.5 text-gray-400" />
              </span>
            </div>
            <span className="text-xs text-gray-400">{loanKpis?.year || new Date().getFullYear()}</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2" data-testid="psc-ytd-value">
            {formatCurrency(loanKpis?.psc_ytd?.amount || 0)}
          </p>
          <p className="text-xs text-gray-400 mt-1">{loanKpis?.psc_ytd?.claims || 0} qualifying claim(s)</p>
        </GlassCard>

        <GlassCard className="p-5" data-testid="tlc-ytd-tile">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Users className="w-4 h-4 text-emerald-600" />
              Total Leader Commission (YTD)
              <span title={loanKpis?.tlc_ytd?.rule || "Total Leader Commission — commission from your direct recruits only, counting claims marked 'submitted' (PC-11)."}
                    data-testid="tlc-info" className="cursor-help">
                <Info className="w-3.5 h-3.5 text-gray-400" />
              </span>
            </div>
            <span className="text-xs text-gray-400">{loanKpis?.year || new Date().getFullYear()}</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2" data-testid="tlc-ytd-value">
            {formatCurrency(loanKpis?.tlc_ytd?.amount || 0)}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {loanKpis?.tlc_ytd?.claims || 0} submitted · {loanKpis?.tlc_ytd?.direct_recruits || 0} direct recruit(s)
          </p>
        </GlassCard>
      </div>

      {/* Rank Progress — Commission Rank Engine V2 (PSC / TLC), replaces the
          legacy gross-sales bar. Promotions are reviewed by admin. */}
      <GlassCard className="border-l-4 border-l-amber-400" data-testid="rank-progress-card">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 rounded-lg bg-amber-50">
            <Award className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">Rank Progress</h3>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
                    data-testid="rank-progress-current-rank">
                {getRankLabel(user?.agent_rank || 'neg')}
              </span>
            </div>
            <p className="text-sm text-gray-500">This year&apos;s ranking credits — reviewed by admin for promotion.</p>
          </div>
          <span className="text-xs text-gray-400">{loanKpis?.year || new Date().getFullYear()}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <div className="rounded-xl bg-indigo-50/60 p-3" data-testid="rank-progress-psc">
            <p className="text-xs text-gray-500">Personal Sales Credit</p>
            <p className="text-lg font-bold text-indigo-700">{formatCurrency(loanKpis?.psc_ytd?.amount || 0)}</p>
            <p className="text-[11px] text-gray-400">{loanKpis?.psc_ytd?.claims || 0} qualifying booking(s)</p>
          </div>
          <div className="rounded-xl bg-emerald-50/60 p-3" data-testid="rank-progress-tlc">
            <p className="text-xs text-gray-500">Total Leader Commission</p>
            <p className="text-lg font-bold text-emerald-700">{formatCurrency(loanKpis?.tlc_ytd?.amount || 0)}</p>
            <p className="text-[11px] text-gray-400">{loanKpis?.tlc_ytd?.direct_recruits || 0} direct recruit(s)</p>
          </div>
        </div>
      </GlassCard>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Pending Appointments */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Pending Appointments</h2>
            <Link to="/appointments">
              <Button variant="ghost" className="text-[#4E50FF] hover:text-[#3436CC]">
                View All <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
          <GlassCard className="space-y-3">
            {pendingAppointments.length > 0 ? (
              pendingAppointments.map((appt) => (
                <div key={appt.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">{appt.customer?.full_name || 'Customer'}</p>
                    <p className="text-sm text-gray-500">{appt.property?.name || 'Property'}</p>
                  </div>
                  <StatusBadge status={appt.status} />
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400">No pending appointments</p>
              </div>
            )}
          </GlassCard>
        </div>

        {/* Recent Bookings */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Bookings</h2>
            <Link to="/bookings">
              <Button variant="ghost" className="text-[#4E50FF] hover:text-[#3436CC]">
                View All <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
          <GlassCard className="space-y-3">
            {recentBookings.length > 0 ? (
              recentBookings.map((booking) => (
                <div key={booking.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">{booking.buyer_name || 'Buyer'}</p>
                    <p className="text-sm text-gray-500">{booking.property?.name || booking.manual_unit_number || 'Property'}</p>
                  </div>
                  <StatusBadge status={booking.status} />
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400">No bookings yet</p>
              </div>
            )}
          </GlassCard>
        </div>
      </div>

      {/* Quick Actions */}
      <GlassCard>
        <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Link to="/agent-properties">
            <Button variant="outline" className="w-full border-gray-200 text-gray-700 hover:bg-[#4E50FF]/5 hover:text-[#4E50FF] hover:border-[#4E50FF]/30" data-testid="quick-properties">
              <Building2 className="w-4 h-4 mr-2" />
              Properties
            </Button>
          </Link>
          <Link to="/appointments">
            <Button variant="outline" className="w-full border-gray-200 text-gray-700 hover:bg-[#4E50FF]/5 hover:text-[#4E50FF] hover:border-[#4E50FF]/30" data-testid="quick-appointments">
              <Calendar className="w-4 h-4 mr-2" />
              Appointments
            </Button>
          </Link>
          <Link to="/bookings">
            <Button variant="outline" className="w-full border-gray-200 text-gray-700 hover:bg-[#4E50FF]/5 hover:text-[#4E50FF] hover:border-[#4E50FF]/30" data-testid="quick-bookings">
              <FileText className="w-4 h-4 mr-2" />
              Bookings
            </Button>
          </Link>
          <Link to="/commission-claims">
            <Button variant="outline" className="w-full border-gray-200 text-gray-700 hover:bg-[#4E50FF]/5 hover:text-[#4E50FF] hover:border-[#4E50FF]/30" data-testid="quick-claims">
              <DollarSign className="w-4 h-4 mr-2" />
              Claims
            </Button>
          </Link>
          <Link to="/wallet">
            <Button variant="outline" className="w-full border-gray-200 text-gray-700 hover:bg-[#4E50FF]/5 hover:text-[#4E50FF] hover:border-[#4E50FF]/30" data-testid="quick-wallet">
              <Wallet className="w-4 h-4 mr-2" />
              Wallet
            </Button>
          </Link>
        </div>
      </GlassCard>
    </div>
  );
};

export default AgentDashboard;
